import { normalizeWord, tokenize } from './words'

/**
 * When a repetition counts (R13, owner decision 2026-10-13).
 *
 * The learner says the sentence `practice.repeatCount` times; while they
 * speak, the on-device recognizer types the words it hears. A repetition
 * counts only when that live text heard at least
 * `practice.repeatMatchThreshold` of the sentence's words, in order.
 *
 * One rule, two implementations — this file and the Android app's — pinned
 * by one table, `backend-nest/docs/repeat-match-cases.json`, which
 * `repeatMatch.test.js` reads. The prose is in `backend-nest/docs/api-contract.md`
 * ("R13 — five repetitions"). Change the table and the contract first.
 */

/** What the recognizer writes for a stretch it could not place in the grammar. */
export const UNKNOWN_WORD = '[unk]'

/** Inside a normalized word, the joints a speaker does not say: «well-known» is two spoken words, «and/or» too. */
const JOINTS = /[-/]/

/**
 * The words of a sentence as a speaker says them, for the recognizer's
 * grammar and for the match: the V2 normalization of every token
 * (`normalizeWord`, the shared dictionary's rule), split at hyphens and
 * slashes, with empty pieces dropped — and any piece containing a digit
 * dropped too, because «2» is said «two» and cannot be checked against what
 * the recognizer writes.
 */
export function spokenWords(textEn) {
  return tokenize(String(textEn ?? '').trim())
    .map(normalizeWord)
    .flatMap((word) => word.split(JOINTS))
    .filter((word) => word.length > 0 && !/\p{Nd}/u.test(word))
}

/** What the recognizer heard, as words the match can compare: normalized, `[unk]` and empty ones dropped. */
export function heardWords(heard) {
  return (heard ?? []).filter((word) => word !== UNKNOWN_WORD).map(normalizeWord).filter((word) => word.length > 0)
}

/**
 * The share (0–1) of the sentence's words that were heard, in order.
 *
 * `target` is `spokenWords(textEn)`; `heard` is what the recognizer typed,
 * one entry per word, in the order it typed them (not yet normalized — pass
 * it through `heardWords` first).
 *
 * The rule the shared table pins: the length of the longest common
 * subsequence of `target` and `heard`, divided by `target.length`; 1 for an
 * empty `target` (nothing to check — a sentence of numbers only).
 *
 * The subsequence, not a bag of words: the order the words were said in
 * matters (shadowing is saying the sentence, not its words), a word the
 * sentence has twice must be heard twice, and saying one word over and
 * over matches it once. One row of the classic dynamic programme is enough
 * — sentences are a few dozen words at most.
 */
export function matchRatio(target, heard) {
  if (target.length === 0) return 1
  // row[j] = LCS of the target words seen so far and heard[0..j)
  const row = new Array(heard.length + 1).fill(0)
  for (const word of target) {
    let diagonal = 0 // row[j - 1] from the previous target word
    for (let j = 1; j <= heard.length; j++) {
      const above = row[j]
      row[j] = word === heard[j - 1] ? diagonal + 1 : Math.max(above, row[j - 1])
      diagonal = above
    }
  }
  return row[heard.length] / target.length
}

/** Does the repetition count under the threshold the server set (`features.repeatMatchThreshold`)? */
export function repetitionCounts(textEn, heard, threshold) {
  if (!(threshold > 0)) return true
  return matchRatio(spokenWords(textEn), heardWords(heard)) >= threshold
}

// ── «همهٔ کلمات» (owner decision 2026-10-15) ───────────────────────────────
//
// A repetition counts only when EVERY word of the sentence was heard, in
// order; words the learner adds cost nothing. Before comparing, both sides
// are brought to one spelling: contractions said in full («I'm» = «I am»,
// «don't» = «do not») and numbers in words («2» = «two»), so a learner is
// never failed for the form of a word. Pinned by the `canonical` and
// `allWords` sections of the same shared table.

/** Contractions whose stem changes when said in full. */
const WHOLE_CONTRACTIONS = {
  "won't": ['will', 'not'],
  "can't": ['can', 'not'],
  "shan't": ['shall', 'not'],
  "ain't": ['is', 'not'],
  "let's": ['let', 'us'],
}

/** Endings said in full; `'s` is read as «is» on both sides, so a possessive matches itself. */
const CONTRACTION_ENDINGS = [
  ["n't", 'not'],
  ["'re", 'are'],
  ["'m", 'am'],
  ["'ve", 'have'],
  ["'ll", 'will'],
  ["'d", 'would'],
  ["'s", 'is'],
]

/** One normalized word with its contraction said in full: `["i", "am"]` for `"i'm"`, the word alone otherwise. */
export function expandContraction(word) {
  if (WHOLE_CONTRACTIONS[word]) return WHOLE_CONTRACTIONS[word]
  for (const [ending, full] of CONTRACTION_ENDINGS) {
    if (word.length > ending.length && word.endsWith(ending)) return [...expandContraction(word.slice(0, -ending.length)), full]
  }
  return [word]
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

function integerWords(n) {
  if (n < 20) return [ONES[n]]
  if (n < 100) return [TENS[Math.floor(n / 10)], ...(n % 10 ? [ONES[n % 10]] : [])]
  if (n < 1000) return [ONES[Math.floor(n / 100)], 'hundred', ...(n % 100 ? integerWords(n % 100) : [])]
  return [...integerWords(Math.floor(n / 1000)), 'thousand', ...(n % 1000 ? integerWords(n % 1000) : [])]
}

/**
 * A number as it is said: «2» → two, «1,250» → one thousand two hundred
 * fifty, «3.5» → three point five. Null for anything else with a digit in it
 * («1st», «10am», a million and up) — such a piece is not checked at all.
 */
export function numberWords(piece) {
  const match = /^(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?$/.exec(piece)
  if (!match) return null
  const n = Number(match[1].replace(/,/g, ''))
  if (!Number.isSafeInteger(n) || n >= 1_000_000) return null
  const words = integerWords(n)
  if (match[2]) words.push('point', ...[...match[2]].map((digit) => ONES[Number(digit)]))
  return words
}

/** One normalized piece in its one spelling: a number in words, a contraction in full. */
function canonicalPiece(word) {
  return /\p{Nd}/u.test(word) ? (numberWords(word) ?? []) : expandContraction(word)
}

/**
 * The words of a sentence as the «all words» rule checks them: `spokenWords`
 * except that numbers are kept, in words, and contractions are said in full.
 */
export function canonicalWords(textEn) {
  return tokenize(String(textEn ?? '').trim())
    .map(normalizeWord)
    .flatMap((word) => word.split(JOINTS))
    .filter((word) => word.length > 0)
    .flatMap(canonicalPiece)
}

/** What the recognizer heard, in the same one spelling (`[unk]` and empty words dropped). */
export function canonicalHeard(heard) {
  return heardWords(heard)
    .flatMap((word) => word.split(JOINTS))
    .filter((word) => word.length > 0)
    .flatMap(canonicalPiece)
}

/**
 * For each word of `target`, whether it was heard: one longest in-order
 * match of `heard` against `target` (the subsequence `matchRatio` counts),
 * walked back from the end — a word that matches is taken, otherwise the
 * target word is dropped when that keeps the match as long, else the heard
 * word. That tie rule is what the shared table pins for which word is
 * marked missing when two could be.
 */
export function wordsHeard(target, heard) {
  const n = target.length
  const m = heard.length
  const table = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      table[i][j] = target[i - 1] === heard[j - 1] ? table[i - 1][j - 1] + 1 : Math.max(table[i - 1][j], table[i][j - 1])
    }
  }
  const marks = new Array(n).fill(false)
  let i = n
  let j = m
  while (i > 0 && j > 0) {
    if (target[i - 1] === heard[j - 1]) {
      marks[i - 1] = true
      i -= 1
      j -= 1
    } else if (table[i - 1][j] >= table[i][j - 1]) {
      i -= 1
    } else {
      j -= 1
    }
  }
  return marks
}

/** The «all words» verdict of one repetition, and which words were missed, for the panel. */
export function allWordsCheck(textEn, heard) {
  const target = canonicalWords(textEn)
  const marks = wordsHeard(target, canonicalHeard(heard))
  return {
    counted: marks.every(Boolean),
    ratio: target.length === 0 ? 1 : marks.filter(Boolean).length / target.length,
    words: target.map((word, index) => ({ word, heard: marks[index] })),
  }
}

/** The recognizer's grammar for a sentence: every way its words may be said — as written and in the one spelling. */
export function grammarWords(textEn) {
  return [...new Set([...spokenWords(textEn), ...canonicalWords(textEn)])]
}

/** One analysis frame for `speechHeard`: 30 ms at 16 kHz. */
const FRAME = 480
/** A frame louder than this (RMS of samples in −1…1) is speech, not room noise. */
const VOICED_RMS = 0.02
/** At least this much voiced audio, in seconds, for a take to have speech in it. */
const MIN_VOICED_SECONDS = 0.3

/**
 * The fallback when there is no recognizer (no model, a browser without
 * WebAssembly or workers): did the take have speech in it at all? `samples`
 * is the take as 16 kHz mono Float32 (`useAudio().samples`).
 */
export function speechHeard(samples) {
  if (!samples?.length) return false
  let voiced = 0
  for (let start = 0; start + FRAME <= samples.length; start += FRAME) {
    let sum = 0
    for (let i = start; i < start + FRAME; i++) sum += samples[i] * samples[i]
    if (Math.sqrt(sum / FRAME) >= VOICED_RMS) voiced += 1
  }
  return (voiced * FRAME) / 16000 >= MIN_VOICED_SECONDS
}
